import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  ExpandMore,
  CheckCircle,
  Error as ErrorIcon,
  HourglassEmpty
} from '@mui/icons-material';
import LogMessage from './LogMessage';

function PhaseSection({ phase, status, logs, duration }) {
  const [expanded, setExpanded] = useState(status === 'in-progress' || status === 'error');

  const getPhaseIcon = () => {
    switch (status) {
      case 'completed':
        return <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />;
      case 'error':
        return <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />;
      case 'in-progress':
        return <CircularProgress size={16} sx={{ color: 'info.main' }} />;
      case 'pending':
      default:
        return <HourglassEmpty sx={{ color: 'action.disabled', fontSize: 20 }} />;
    }
  };

  const getPhaseColor = () => {
    switch (status) {
      case 'completed':
        return 'success.main';
      case 'error':
        return 'error.main';
      case 'in-progress':
        return 'info.main';
      case 'pending':
      default:
        return 'action.disabled';
    }
  };

  const getStatusLabel = () => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'error':
        return 'Error';
      case 'in-progress':
        return 'In Progress';
      case 'pending':
      default:
        return 'Pending';
    }
  };

  const phaseTitles = {
    downloading: 'Downloading File',
    parsing: 'Parsing Data',
    processing: 'Processing Records',
    calculating: 'Calculating Payments',
    saving: 'Saving Results'
  };

  // Auto-expand when status changes to in-progress or error
  React.useEffect(() => {
    if (status === 'in-progress' || status === 'error') {
      setExpanded(true);
    }
  }, [status]);

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{
        mb: 1,
        borderLeft: 3,
        borderColor: getPhaseColor(),
        '&:before': {
          display: 'none'
        }
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          '& .MuiAccordionSummary-content': {
            alignItems: 'center'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <Box sx={{ mr: 2 }}>
            {getPhaseIcon()}
          </Box>
          <Typography sx={{ fontWeight: 600, flex: 1 }}>
            {phaseTitles[phase] || phase}
          </Typography>
          <Chip
            label={getStatusLabel()}
            size="small"
            sx={{
              bgcolor: `${getPhaseColor()}20`,
              color: getPhaseColor(),
              fontWeight: 500,
              mr: 1
            }}
          />
          {logs.length > 0 && (
            <Chip
              label={`${logs.length} logs`}
              size="small"
              variant="outlined"
              sx={{ mr: 1 }}
            />
          )}
          {duration && status === 'completed' && (
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
              {formatDuration(duration)}
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          bgcolor: 'grey.50',
          maxHeight: 300,
          overflowY: 'auto',
          p: 2
        }}
      >
        {logs.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No logs yet...
          </Typography>
        ) : (
          <Box>
            {logs.map((log, index) => (
              <LogMessage
                key={index}
                message={log.message}
                type={log.type}
                timestamp={log.timestamp}
              />
            ))}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

function formatDuration(ms) {
  if (ms < 1000) return `${ms}ms`;
  const seconds = (ms / 1000).toFixed(1);
  return `${seconds}s`;
}

export default PhaseSection;

